import type { SeedIsland } from './seedIslands'

/**
 * The five identity islands, at HSK 1–2.
 *
 * These are the same five topics as the standard set — work, why you study,
 * family, where you live, free time — because that is the Shekhtman core: the
 * things you will be asked in the first two minutes of every conversation you
 * ever have in this language. What changes is the length.
 *
 * The standard versions run to 20 characters and use 一开始…后来就, 只有…才,
 * 觉得…最. They are good blocks and completely wrong as the first thing a
 * beginner meets: a learner two weeks in cannot produce them, cannot hear them
 * back, and concludes the method is not for them. Blocks here are four to eight
 * characters and stay inside HSK 1–2 vocabulary and grammar.
 *
 * Short does not mean babyish. Every block is still a whole thing an adult
 * would actually say to another adult — 我在一家公司工作 is not a simplified
 * sentence, it is just a short one. The method needs blocks you can deliver
 * without assembly, and a short block you own beats a long one you can only
 * read.
 *
 * Pinyin is stored as SPOKEN, with 一/不 sandhi applied, matching the rest of
 * the seed data. The grader exempts those two syllables from tone checking.
 */
export const BEGINNER_ZH: SeedIsland[] = [
  {
    id: 'isl_work_a1',
    title: 'My work (starting out)',
    titleZh: '我做什么工作',
    useWhen: 'The second question anyone asks. Five short answers, ready cold.',
    tags: ['introductions', 'work', 'beginner'],
    hskLevel: 1,
    blocks: [
      [
        '我在一家公司工作。',
        'wǒ zài yì jiā gōng sī gōng zuò',
        'I work at a company.',
        '在…工作 is the frame. Swap 公司 for 学校, 医院, 银行 and you have four more answers.',
      ],
      [
        '我做电脑方面的工作。',
        'wǒ zuò diàn nǎo fāng miàn de gōng zuò',
        'I work in computers.',
        '…方面的工作 — "work in the area of". Vaguer than a job title and far more useful when you do not know the word for yours.',
      ],
      ['我很喜欢我的工作。', 'wǒ hěn xǐ huan wǒ de gōng zuò', 'I like my job.'],
      [
        '我每天八点上班。',
        'wǒ měi tiān bā diǎn shàng bān',
        'I start work at eight every day.',
        '上班 is going to work; 下班 is leaving. Neither takes an object.',
      ],
      ['工作有点儿忙。', 'gōng zuò yǒu diǎnr máng', 'Work is a bit busy.'],
    ],
  },

  {
    id: 'isl_why_a1',
    title: 'Why I study Chinese (starting out)',
    titleZh: '我学中文',
    useWhen: 'You will be asked this every single time. Have an answer that is one sentence long.',
    tags: ['introductions', 'study', 'beginner'],
    hskLevel: 2,
    blocks: [
      ['我学中文两年了。', 'wǒ xué zhōng wén liǎng nián le', "I've been studying Chinese for two years."],
      [
        '因为我的工作需要。',
        'yīn wèi wǒ de gōng zuò xū yào',
        'Because my work needs it.',
        'A complete answer on its own in speech. The 所以 half that a textbook demands is not required when the question was "why".',
      ],
      [
        '我觉得中文很有意思。',
        'wǒ jué de zhōng wén hěn yǒu yì si',
        'I find Chinese really interesting.',
      ],
      [
        '说话还可以，写字很难。',
        'shuō huà hái kě yǐ xiě zì hěn nán',
        'Speaking is okay, writing is hard.',
        '还可以 — "okay, not bad". One of the most useful two words in the language.',
      ],
      ['我要多说，多听。', 'wǒ yào duō shuō duō tīng', 'I need to speak more and listen more.'],
    ],
  },

  {
    id: 'isl_family_a1',
    title: 'My family (starting out)',
    titleZh: '我的家',
    useWhen: 'Asked within minutes, everywhere, by everyone.',
    tags: ['introductions', 'family', 'beginner'],
    hskLevel: 1,
    blocks: [
      [
        '我家有四个人。',
        'wǒ jiā yǒu sì ge rén',
        'There are four people in my family.',
        '家 alone means both the household and the house. No word for "members" is needed.',
      ],
      ['我有一个儿子。', 'wǒ yǒu yí ge ér zi', 'I have a son.'],
      ['我女儿今年十岁。', 'wǒ nǚ ér jīn nián shí suì', 'My daughter is ten this year.'],
      [
        '我太太是老师。',
        'wǒ tài tai shì lǎo shī',
        'My wife is a teacher.',
        'No 一个 before a profession after 是. 我太太是一个老师 is what a learner says; 我太太是老师 is what a speaker says.',
      ],
      ['他们都在美国。', 'tā men dōu zài měi guó', "They're all in America."],
    ],
  },

  {
    id: 'isl_where_a1',
    title: 'Where I live (starting out)',
    titleZh: '我住在哪儿',
    useWhen: 'Follows the name and the job, every time.',
    tags: ['introductions', 'place', 'beginner'],
    hskLevel: 1,
    blocks: [
      [
        '我住在美国。',
        'wǒ zhù zài měi guó',
        'I live in America.',
        '住在 — the place comes after 在, never before the verb.',
      ],
      ['我是美国人。', 'wǒ shì měi guó rén', "I'm American."],
      [
        '那是一个小城市。',
        'nà shì yí ge xiǎo chéng shì',
        "It's a small city.",
        '一 is yí — second tone — because the 个 after it is fourth. Before any other tone it is yì.',
      ],
      [
        '我在那儿住了十年了。',
        'wǒ zài nàr zhù le shí nián le',
        "I've lived there ten years.",
        'The second 了 is what makes it still true. Without it, 住了十年 is a stay that ended.',
      ],
      ['我很喜欢那个地方。', 'wǒ hěn xǐ huan nà ge dì fang', 'I like it there a lot.'],
    ],
  },

  {
    id: 'isl_free_a1',
    title: 'What I do outside work (starting out)',
    titleZh: '我喜欢做什么',
    useWhen: 'The question that follows every answer about your job.',
    tags: ['introductions', 'hobbies', 'beginner'],
    hskLevel: 2,
    blocks: [
      ['我喜欢看书和听音乐。', 'wǒ xǐ huan kàn shū hé tīng yīn yuè', 'I like reading and listening to music.'],
      [
        '周末我常常去走路。',
        'zhōu mò wǒ cháng cháng qù zǒu lù',
        'At weekends I often go walking.',
        'The time word goes first. 周末 before 我, not after — this is the ordering learners get wrong most.',
      ],
      ['我在学做中国菜。', 'wǒ zài xué zuò zhōng guó cài', "I'm learning to cook Chinese food."],
      [
        '我做得不太好。',
        'wǒ zuò de bú tài hǎo',
        "I'm not very good at it.",
        '不 is bú before the fourth-tone 太. 不太 is the polite way to say "not really".',
      ],
      ['你呢？你喜欢什么？', 'nǐ ne nǐ xǐ huan shén me', 'And you? What do you like?'],
    ],
  },
  {
    id: 'isl_jieshao_a1',
    title: 'Introducing yourself',
    titleZh: '自我介绍',
    useWhen:
      'The first thirty seconds with anyone new — you give your name, ask for theirs, say where you are from and what you do here, and close the exchange politely.',
    tags: ['introductions', 'greetings', 'first-meeting'],
    hskLevel: 1,
    blocks: [
      ['你好，我叫大卫。', 'nǐ hǎo wǒ jiào dà wèi', 'Hi, I\'m David.'],
      ['你叫什么名字？', 'nǐ jiào shén me míng zi', 'What\'s your name?'],
      [
        '我是美国人，你呢？',
        'wǒ shì měi guó rén nǐ ne',
        'I\'m American — how about you?',
        '你呢 hands the question back without repeating it. It works after almost any statement about yourself, so it is the cheapest way to keep a first conversation moving.',
      ],
      ['我在北京工作。', 'wǒ zài běi jīng gōng zuò', 'I work in Beijing.'],
      [
        '我的中文不太好。',
        'wǒ de zhōng wén bú tài hǎo',
        'My Chinese isn\'t great.',
        '不太 is the modest version — 不好 alone lands as a flat verdict. Note the tone change: 不 becomes bú before the fourth-tone 太.',
      ],
      [
        '很高兴认识你。',
        'hěn gāo xìng rèn shi nǐ',
        'Good to meet you.',
        'The same line does double duty: you say it when you meet someone and again when you part.',
      ],
    ],
  },
  {
    id: 'isl_dian_a1',
    title: 'Ordering a coffee',
    titleZh: '点一杯咖啡',
    useWhen:
      'You\'re at a counter ordering for yourself and one other person, and you want the whole exchange — what you want, the size, here or to go, paying, chasing the cup — to come out without assembling it.',
    tags: ['coffee', 'ordering', 'everyday'],
    hskLevel: 1,
    blocks: [
      ['我要一杯热咖啡。', 'wǒ yào yì bēi rè kā fēi', 'I\'d like a hot coffee.'],
      [
        '大杯的，谢谢。',
        'dà bēi de xiè xie',
        'Make it a large one, thanks.',
        '的 stands in for the thing you already named, so you never repeat 咖啡. Same trick for 小杯的, 热的, 冰的.',
      ],
      ['我朋友要一杯茶。', 'wǒ péng you yào yì bēi chá', 'My friend will have a tea.'],
      [
        '在这里喝，谢谢。',
        'zài zhè lǐ hē xiè xie',
        'For here, thanks.',
        'The counter puts it as a choice — 在这里喝还是带走？(chain shops often say 堂食还是外带？) — and you hand back the half you want. Leave 我 off: with a subject it sounds like you\'re separating yourself from whoever you walked in with.',
      ],
      ['这些多少钱？', 'zhè xiē duō shao qián', 'How much is all this?'],
      [
        '请问，我的咖啡呢？',
        'qǐng wèn wǒ de kā fēi ne',
        'Excuse me, where\'s my coffee?',
        '呢 after a noun asks where that thing is. No verb, no 在哪里 — just name it and add 呢.',
      ],
    ],
  },
]
